/**
 * Funding resource register (P429.2).
 *
 * What money moves through or rests in -- settlement accounts,
 * facilities, buffers, market access, counterparties. An admitted
 * service draws on these (the edge lives on the service drawer), and
 * the layer view reads them as the funding layer.
 */
import type { AdminColumn, EntityAdapter, FormField } from "./types";

export type FundingKind =
  "settlement_account" | "facility" | "buffer" | "market_access" | "counterparty";

const KIND_LABELS: Record<FundingKind, string> = {
  settlement_account: "Settlement account",
  facility: "Facility",
  buffer: "Buffer",
  market_access: "Market access",
  counterparty: "Counterparty",
};

export interface FundingResource {
  id: string;
  code: string;
  name: string;
  kind: FundingKind;
  owner: string | null;
  required_level: string | null;
  description: string | null;
  created_at: string;
  updated_at: string;
}

export interface FundingResourceIn {
  code: string;
  name: string;
  kind: FundingKind;
  owner: string | null;
  required_level: string | null;
  description: string | null;
}

export type FundingResourcePatch = Partial<Omit<FundingResourceIn, "code">>;

const columns: AdminColumn<FundingResource>[] = [
  {
    key: "name",
    label: "Funding resource",
    sortValue: (r) => r.name,
    render: (r) => (
      <span>
        <span className="font-medium text-slate-900">{r.name}</span>
        <span className="ms-2 font-mono text-xs text-slate-500">
          <span aria-hidden="true"> · </span>
          {r.code}
        </span>
      </span>
    ),
  },
  {
    key: "kind",
    label: "Kind",
    sortValue: (r) => r.kind,
    render: (r) => (
      <span className="text-xs text-slate-700">{KIND_LABELS[r.kind]}</span>
    ),
  },
  {
    key: "required_level",
    label: "Level a dependent needs",
    render: (r) =>
      r.required_level ? (
        <span className="text-xs text-slate-700">{r.required_level}</span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
  {
    key: "owner",
    label: "Owner",
    render: (r) =>
      r.owner ? (
        <span className="text-xs text-slate-700">{r.owner}</span>
      ) : (
        <span className="text-xs text-slate-400">--</span>
      ),
  },
];

const formFields: FormField<FundingResourceIn>[] = [
  { kind: "text", key: "code", label: "Code", required: true, maxLength: 64 },
  { kind: "text", key: "name", label: "Name", required: true, maxLength: 255 },
  {
    kind: "select",
    key: "kind",
    label: "Kind",
    required: true,
    options: (Object.keys(KIND_LABELS) as FundingKind[]).map((v) => ({
      value: v,
      label: KIND_LABELS[v],
    })),
  },
  { kind: "text", key: "owner", label: "Owner", maxLength: 255 },
  { kind: "text", key: "required_level", label: "Level a dependent needs" },
  { kind: "textarea", key: "description", label: "Description", rows: 3 },
];

export const fundingResourceAdapter: EntityAdapter<
  FundingResource,
  FundingResourceIn,
  FundingResourcePatch
> = {
  i18nPrefix: "modules.foundation.admin.fundingResources",
  importKind: "funding_resource",
  endpoint: "/v1/admin/foundation/funding-resources",
  queryKey: ["foundation-admin", "funding-resources"] as const,
  columns,
  defaultSort: { key: "name", dir: "asc" },
  rowKey: (r) => r.id,
  rowSearchHaystack: (r) => `${r.code} ${r.name} ${r.kind} ${r.owner ?? ""}`,
  formFields,
  formInitial: () => ({
    code: "",
    name: "",
    kind: "settlement_account",
    owner: null,
    required_level: null,
    description: null,
  }),
  rowToPatch: (r) => ({
    name: r.name,
    kind: r.kind,
    owner: r.owner,
    required_level: r.required_level,
    description: r.description,
  }),
};
